import {
  FormControl,
  Grid,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@material-ui/core";
import React from "react";
import { SocItem } from "../../domain/SocItem";
import { SectionProps } from "./SectionProps";
import { Error } from "../common/Error";
import { Controller, useWatch } from "react-hook-form";
import { fieldLabelsOfqual } from "./fieldLabels";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  complianceDisagreementService,
  ragRatingService,
  followupService,
  followupStatusService,
  regulatoryComplianceService,
} from "../../services";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";

export const ComplianceDeclaration: React.FC<SectionProps<SocItem>> = (
  sectionProps
) => {
  const { control, disabled, errors, clearErrors } = sectionProps;

  const { agreeCurrentCompliant, agreeFutureCompliant } = useWatch<SocItem>({
    control,
    name: ["agreeCurrentCompliant", "agreeFutureCompliant"],
  });

  const [
    complianceDisagreementLookups,
    ragRatingLookups,
    followupLookups,
    followupStatusLookups,
    regComplianceClosureStatusLookups
  ] = useLookup(
    complianceDisagreementService,
    ragRatingService,
    followupService,
    followupStatusService,
    regulatoryComplianceService
  );


  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="agreeCurrentCompliant"
          rules={{ validate: (value) => value !== null }}
          required
          render={(props) => (
            <FormControl component="fieldset" required disabled={disabled}>
              <FormLabel id="agreeCurrentCompliant-label" component="legend">
                {fieldLabelsOfqual.agreeCurrentCompliant}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="agreeCurrentCompliant-label"
                name="agreeCurrentCompliant"
                data-test="radiogroup-agreeCurrentCompliant"
                onChange={(e) => {
                  clearErrors("agreeCurrentCompliant");
                  clearErrors("complianceDisagreement");
                  props.onChange(e.target.value === "true");
                }}
              >
                <FormControlLabel
                  value={true}
                  label="Yes"
                  control={<Radio />}
                  data-test="agreeCurrentCompliant-yes"
                  checked={props.value === true}
                />
                <FormControlLabel
                  value={false}
                  label="No"
                  control={<Radio />}
                  data-test="agreeCurrentCompliant-no"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        {errors.agreeCurrentCompliant && (
          <>
            <br></br>
            <Error data-test="agreeCurrentCompliant-validation">
              You must select at least one option
            </Error>
          </>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="agreeFutureCompliant"
          rules={{ validate: (value) => value !== null }}
          required
          render={(props) => (
            <FormControl component="fieldset" required disabled={disabled}>
              <FormLabel id="agreeFutureCompliant-label" component="legend">
                {fieldLabelsOfqual.agreeFutureCompliant}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="agreeFutureCompliant-label"
                name="agreeFutureCompliant"
                data-test="radiogroup-agreeFutureCompliant"
                onChange={(e) => {
                  clearErrors("agreeFutureCompliant");
                  clearErrors("complianceDisagreement");
                  props.onChange(e.target.value === "true");
                }}
              >
                <FormControlLabel
                  value={true}
                  label="Yes"
                  control={<Radio />}
                  data-test="agreeFutureCompliant-yes"
                  checked={props.value === true}
                />
                <FormControlLabel
                  value={false}
                  label="No"
                  control={<Radio />}
                  data-test="agreeFutureCompliant-no"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
        {errors.agreeFutureCompliant && (
          <>
            <br></br>
            <Error data-test="agreeFutureCompliant-validation">
              You must select at least one option
            </Error>
          </>
        )}
      </Grid>
      {agreeCurrentCompliant === false || agreeFutureCompliant === false ? (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="complianceDisagreement"
              render={({ onChange, ...props }) => (
                <Select
                  label={fieldLabelsOfqual.complianceDisagreement}
                  readOnly={disabled}
                  datatest="complianceDisagreement-field"
                  required
                  onChange={(e) => {
                    if (e.target.value !== null) {
                      clearErrors("complianceDisagreement");
                    }
                    onChange(e);
                  }}
                  {...props}
                  defaultValue={null}
                >
                  {complianceDisagreementLookups}
                </Select>
              )}
            />
            {errors.complianceDisagreement?.type === "required" && (
              <Error data-test="complianceDisagreement-validation">
                This field is required
              </Error>
            )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="complianceDisagreementDescription"
              render={({ onChange, value }) => (
                <>
                  <label>
                    {fieldLabelsOfqual.complianceDisagreementDescription}
                  </label>
                  <MarkdownEditor
                    onChange={onChange}
                    value={value}
                    dataTest="reasonsForDisagreement-freetextField"
                  />
                </>
              )}
            />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="complianceRagRating"
          rules={{ required: true }}
          render={({ onChange, ...props }) => (
            <Select
              label={fieldLabelsOfqual.complianceRagRating}
              readOnly={disabled}
              datatest="complianceRagRating-field"
              required
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("complianceRagRating");
                }
                onChange(e);
              }}
              {...props}
            >
              {ragRatingLookups}
            </Select>
          )}
        />
        {errors.complianceRagRating?.type === "required" && (
          <Error data-test="complianceRagRating-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="complianceFollowUp"
          rules={{ required: true }}
          render={({ onChange, ...props }) => (
            <Select
              label={fieldLabelsOfqual.complianceFollowUp}
              readOnly={disabled}
              datatest="standardsFollowUp-field"
              required
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("complianceFollowUp");
                }
                onChange(e);
              }}
              {...props}
            >
              {followupLookups}
            </Select>
          )}
        />
        {errors.complianceFollowUp?.type === "required" && (
          <Error data-test="complianceFollowUp-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="complianceFollowUpStatus"
          rules={{ required: true }}
          render={({ onChange, ...props }) => (
            <Select
              label={fieldLabelsOfqual.complianceFollowUpStatus}
              readOnly={disabled}
              datatest="standardsFollowUpStatus-field"
              required
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("complianceFollowUpStatus");
                }
                onChange(e);
              }}
              {...props}
            >
              {followupStatusLookups}
            </Select>
          )}
        />
        {errors.complianceFollowUpStatus?.type === "required" && (
          <Error data-test="complianceFollowUpStatus-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="complianceClosureStatus"
          rules={{ required: true }}
          render={({ onChange, ...props }) => (
            <Select
              label={fieldLabelsOfqual.complianceClosureStatus}
              readOnly={disabled}
              datatest="standardsClosureStatus-field"
              required
              onChange={(e) => {
                if (e.target.value !== null) {
                  clearErrors("complianceClosureStatus");
                }
                onChange(e);
              }}
              {...props}
            >
              {regComplianceClosureStatusLookups}
            </Select>
          )}
        />
        {errors.complianceClosureStatus?.type === "required" && (
          <Error data-test="standardsClosureStatus-validation">
            This field is required
          </Error>
        )}
      </Grid>
    </Grid>
  );
};
